import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { workOrderProgress, STATUS_META } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Wrench,
  Users,
  Car,
  Package,
  ClipboardList,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  ShoppingCart,
  Clock,
  Store,
} from "lucide-react";

const STAT_TILES = [
  { key: "open_orders", label: "Open Repairs", icon: Wrench, tint: "#E5A82E", testid: "stat-open-orders", to: "/work-orders" },
  { key: "in_progress_orders", label: "In Progress", icon: Clock, tint: "#F2CC8F", testid: "stat-in-progress", to: "/work-orders?status=in_progress" },
  { key: "ready_orders", label: "Ready for Pickup", icon: CheckCircle2, tint: "#81B29A", testid: "stat-ready-orders", to: "/work-orders?status=ready" },
  { key: "pending_parts", label: "Parts to Order", icon: ShoppingCart, tint: "#3D405B", testid: "stat-pending-parts", to: "/parts-to-order" },
];

const MINI_TILES = [
  { key: "total_dealers", label: "Dealers", icon: Store, to: "/dealers", testid: "stat-dealers" },
  { key: "total_customers", label: "Customers", icon: Users, to: "/customers", testid: "stat-customers" },
  { key: "total_vehicles", label: "Vehicles", icon: Car, to: "/vehicles", testid: "stat-vehicles" },
  { key: "low_stock_parts", label: "Low-Stock Parts", icon: AlertCircle, to: "/parts", testid: "stat-low-stock" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [customers, setCustomers] = useState({});
  const [vehicles, setVehicles] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [s, r, c, v] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/dashboard/recent-orders"),
      api.get("/customers"),
      api.get("/vehicles"),
    ]);
    setStats(s.data);
    setRecent(r.data);
    setCustomers(Object.fromEntries(c.data.map((x) => [x.id, x])));
    setVehicles(Object.fromEntries(v.data.map((x) => [x.id, x])));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <PageHeader
        testid="dashboard-header"
        title="Welcome back"
        subtitle="Here's what's happening in the shop today."
        actions={
          <Link to="/work-orders">
            <Button
              data-testid="dashboard-new-order-btn"
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl font-medium shadow-sm"
            >
              <Wrench className="w-4 h-4 mr-2" /> New Repair Order
            </Button>
          </Link>
        }
      />

      {/* Hero banner */}
      <div
        className="relative overflow-hidden rounded-2xl mb-8 border border-warm shadow-card bg-gradient-to-r from-[#1A1A1A] via-[#141414] to-[#000]"
        data-testid="dashboard-hero"
      >
        <img
          src="https://images.unsplash.com/photo-1618312980096-873bd19759a0?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="relative p-8 md:p-10 max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-[#E5A82E] font-semibold mb-2">
            Today at Car Dream
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white leading-tight mb-2">
            Keep the wheels turning.
          </h2>
          <p className="text-white/80">
            Track every repair from drop-off to pickup — vehicles, tasks, and parts orders in one place.
          </p>
        </div>
      </div>

      {/* Primary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {STAT_TILES.map(({ key, label, icon: Icon, tint, testid, to, money }) => (
          <Link key={key} to={to} data-testid={testid}>
            <Card
              className="p-6 rounded-2xl border-warm shadow-card bg-white hover:shadow-floating hover:-translate-y-0.5 transition-all duration-200 cursor-pointer h-full"
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${tint}1A`, color: tint }}
                >
                  <Icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <ArrowRight className="w-4 h-4 text-slate-muted opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="text-xs uppercase tracking-widest text-slate-muted font-semibold mb-1">
                {label}
              </div>
              <div className="font-heading text-3xl font-bold text-[#2B2D42]">
                {loading ? <Skeleton className="h-8 w-16" /> : stats?.[key] ?? 0}
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Mini tiles + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          {MINI_TILES.map(({ key, label, icon: Icon, to, testid }) => (
            <Link key={key} to={to} data-testid={testid}>
              <Card className="p-5 rounded-2xl border-warm shadow-card bg-white hover:bg-[#FAF9F6] transition-colors flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#F3F2EE] flex items-center justify-center text-[#3D405B]">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-slate-muted font-semibold">
                    {label}
                  </div>
                  <div className="font-heading text-xl font-bold text-[#2B2D42]">
                    {loading ? <Skeleton className="h-5 w-10" /> : stats?.[key] ?? 0}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-muted" />
              </Card>
            </Link>
          ))}
        </div>

        <Card className="lg:col-span-2 p-6 rounded-2xl border-warm shadow-card bg-white" data-testid="recent-orders-card">
          <div className="flex items-center justify-between pb-4 border-b border-[#EAE8E3] mb-4">
            <h3 className="font-heading text-xl font-semibold text-[#2B2D42]">
              Recent Repair Orders
            </h3>
            <Link
              to="/work-orders"
              className="text-sm font-medium text-[#E5A82E] hover:underline"
              data-testid="view-all-orders"
            >
              View all
            </Link>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : recent.length === 0 ? (
            <div className="py-10 text-center text-slate-muted" data-testid="no-recent-orders">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No repair orders yet. Create your first one from the Repair Orders page.
            </div>
          ) : (
            <div className="space-y-2">
              {recent.map((wo) => {
                const p = workOrderProgress(wo);
                const cust = customers[wo.customer_id];
                const veh = vehicles[wo.vehicle_id];
                const meta = STATUS_META[wo.status] || STATUS_META.new;
                return (
                  <Link
                    key={wo.id}
                    to={`/work-orders/${wo.id}`}
                    data-testid={`recent-order-${wo.order_number}`}
                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-[#FAF9F6] transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#F3F2EE] flex items-center justify-center text-[#3D405B]">
                      <Wrench className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-slate-muted">{wo.order_number}</span>
                        {veh?.license_plate ? (
                          <span className="font-mono text-sm font-semibold text-[#1A1A1A] bg-[#F4D35E]/30 border border-[#E5A82E]/40 rounded-md px-2 py-0.5">
                            {veh.license_plate.toUpperCase()}
                          </span>
                        ) : (
                          <span className="font-medium text-[#2B2D42]">{cust?.name || "—"}</span>
                        )}
                      </div>
                      <div className="text-sm text-slate-muted truncate mt-0.5">
                        {veh ? `${veh.year || ""} ${veh.make} ${veh.model}`.trim() : ""} {cust?.name ? `· ${cust.name}` : ""} · {wo.complaint || "No complaint noted"}
                      </div>
                    </div>
                    <div className="hidden sm:block w-24 text-xs text-slate-muted">
                      {p.total === 0 ? "No tasks" : `${p.done}/${p.total} done`}
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${meta.cls}`}>
                      {meta.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
